import { API_BASE } from "../config";
import { formatDateOnly } from '../utils/dates';
// =============================================================================
// File: frontend/src/components/CarbonFootprintDashboard.jsx
// NEW FILE - Sprint 2
// Carbon Footprint Dashboard - Environmental Impact Tracking UI
// =============================================================================

import React, { useState, useEffect } from 'react';

// Grade colors
const GRADE_COLORS = {
  'A+': { bg: '#D1FAE5', text: '#065F46', border: '#059669' },
  'A': { bg: '#D1FAE5', text: '#065F46', border: '#059669' },
  'B': { bg: '#D1FAE5', text: '#065F46', border: '#059669' },
  'C': { bg: '#FEF3C7', text: '#92400E', border: '#F59E0B' },
  'D': { bg: '#FED7AA', text: '#9A3412', border: '#F97316' },
  'E': { bg: '#FEE2E2', text: '#991B1B', border: '#EF4444' },
  'N/A': { bg: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' }
};

const CarbonFootprintDashboard = ({ token, fetchWithAuth: fetchWithAuthProp, onClose }) => {
  // State
  const [activeView, setActiveView] = useState('today'); // today, weekly, monthly, history
  const [todayData, setTodayData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch helper — delegates to prop fetchWithAuth (with 401 retry) if available
  const fetchWithAuth = fetchWithAuthProp || (async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    return response;
  });

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load all data in parallel
      const [todayRes, weeklyRes, monthlyRes, historyRes] = await Promise.all([
        fetchWithAuth(`${API_BASE}/api/carbon/today`),
        fetchWithAuth(`${API_BASE}/api/carbon/weekly?weeks_ago=0`),
        fetchWithAuth(`${API_BASE}/api/carbon/monthly`),
        fetchWithAuth(`${API_BASE}/api/carbon/history?days=30`)
      ]);

      if (todayRes.ok) setTodayData(await todayRes.json());
      if (weeklyRes.ok) setWeeklyData(await weeklyRes.json());
      if (monthlyRes.ok) setMonthlyData(await monthlyRes.json());
      if (historyRes.ok) setHistoryData(await historyRes.json());
    } catch (err) {
      setError('Failed to load carbon data');
    } finally {
      setLoading(false);
    }
  };

  const aggregateToday = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/carbon/aggregate-today`, {
        method: 'POST'
      });
      if (response.ok) {
        await loadData();
      }
    } catch (err) {
      setError('Failed to aggregate data');
    }
  };

  // Get grade color
  const getGradeStyle = (grade) => {
    return GRADE_COLORS[grade] || GRADE_COLORS['N/A'];
  };

  // Render today view
  const renderTodayView = () => {
    if (!todayData) return <div className="cfd-emptyState">No data for today</div>;

    const gradeStyle = getGradeStyle(todayData.daily_grade || 'N/A');
    const breakdown = todayData.score_breakdown || {};
    const total = Object.values(breakdown).reduce((a, b) => a + b, 0);

    return (
      <div>
        {/* Grade Card */}
        <div className="cfd-gradeCard" style={{ backgroundColor: gradeStyle.bg, border: `2px solid ${gradeStyle.border}` }}>
          <div className="cfd-gradeLabel" style={{ color: gradeStyle.text }}>Today's Eco Grade</div>
          <div className="cfd-gradeValue" style={{ color: gradeStyle.text }}>
            {todayData.daily_grade || '\u2014'}
          </div>
          <div className="cfd-gradeSubtext" style={{ color: gradeStyle.text }}>
            {todayData.total_items_logged} items logged
          </div>
        </div>

        {/* Score Breakdown */}
        {total > 0 && (
          <div className="cfd-breakdownSection">
            <div className="cfd-sectionTitle">Score Breakdown</div>
            <div className="cfd-breakdownBar">
              {breakdown.A > 0 && (
                <div className="cfd-breakdownSegment cfd-segmentA" style={{ width: `${(breakdown.A / total) * 100}%` }}>
                  {breakdown.A}
                </div>
              )}
              {breakdown.B > 0 && (
                <div className="cfd-breakdownSegment cfd-segmentB" style={{ width: `${(breakdown.B / total) * 100}%` }}>
                  {breakdown.B}
                </div>
              )}
              {breakdown.C > 0 && (
                <div className="cfd-breakdownSegment cfd-segmentC" style={{ width: `${(breakdown.C / total) * 100}%` }}>
                  {breakdown.C}
                </div>
              )}
              {breakdown.D > 0 && (
                <div className="cfd-breakdownSegment cfd-segmentD" style={{ width: `${(breakdown.D / total) * 100}%` }}>
                  {breakdown.D}
                </div>
              )}
              {breakdown.E > 0 && (
                <div className="cfd-breakdownSegment cfd-segmentE" style={{ width: `${(breakdown.E / total) * 100}%` }}>
                  {breakdown.E}
                </div>
              )}
              {breakdown.unscored > 0 && (
                <div className="cfd-breakdownSegment cfd-segmentUnscored" style={{ width: `${(breakdown.unscored / total) * 100}%` }}>
                  {breakdown.unscored}
                </div>
              )}
            </div>
            <div className="cfd-breakdownLegend">
              <div className="cfd-legendItem"><span className="cfd-legendDot cfd-dotA" /> A: {breakdown.A || 0}</div>
              <div className="cfd-legendItem"><span className="cfd-legendDot cfd-dotB" /> B: {breakdown.B || 0}</div>
              <div className="cfd-legendItem"><span className="cfd-legendDot cfd-dotC" /> C: {breakdown.C || 0}</div>
              <div className="cfd-legendItem"><span className="cfd-legendDot cfd-dotD" /> D: {breakdown.D || 0}</div>
              <div className="cfd-legendItem"><span className="cfd-legendDot cfd-dotE" /> E: {breakdown.E || 0}</div>
              <div className="cfd-legendItem"><span className="cfd-legendDot cfd-dotUnscored" /> Unscored: {breakdown.unscored || 0}</div>
            </div>
          </div>
        )}

        {/* Sources */}
        <div className="cfd-statsGrid">
          <div className="cfd-statCard">
            <div className="cfd-statValue">{todayData.sources?.pantry_items || 0}</div>
            <div className="cfd-statLabel">Pantry Items</div>
          </div>
          <div className="cfd-statCard">
            <div className="cfd-statValue">{todayData.sources?.recipes || 0}</div>
            <div className="cfd-statLabel">Recipes</div>
          </div>
          <div className="cfd-statCard">
            <div className="cfd-statValue">{todayData.sources?.shopping_lists || 0}</div>
            <div className="cfd-statLabel">Shopping</div>
          </div>
        </div>

        <button
          onClick={aggregateToday}
          className="cfd-refreshButton"
        >
          Refresh Today's Data
        </button>
      </div>
    );
  };

  // Render weekly view
  const renderWeeklyView = () => {
    if (!weeklyData) return <div className="cfd-emptyState">No weekly data</div>;

    const gradeStyle = getGradeStyle(weeklyData.average_grade || 'N/A');
    const trendColors = {
      improving: { bg: '#D1FAE5', text: '#065F46', icon: '\u{1F4C8}' },
      stable: { bg: '#F3F4F6', text: '#374151', icon: '\u27A1\uFE0F' },
      declining: { bg: '#FEF2F2', text: '#991B1B', icon: '\u{1F4C9}' },
      insufficient_data: { bg: '#F3F4F6', text: '#6B7280', icon: '\u2753' }
    };
    const trend = trendColors[weeklyData.grade_trend] || trendColors.insufficient_data;

    return (
      <div>
        {/* Header */}
        <div className="cfd-weeklyHeader">
          <div className="cfd-gradeCard cfd-weeklyGradeCard" style={{ backgroundColor: gradeStyle.bg, border: `2px solid ${gradeStyle.border}` }}>
            <div className="cfd-gradeLabel" style={{ color: gradeStyle.text }}>Weekly Average</div>
            <div className="cfd-gradeValue cfd-weeklyGradeValue" style={{ color: gradeStyle.text }}>
              {weeklyData.average_grade || '\u2014'}
            </div>
          </div>
          <div className="cfd-weeklyInfoPanel">
            <div className="cfd-trendBadge" style={{ backgroundColor: trend.bg, color: trend.text }}>
              {trend.icon} {weeklyData.grade_trend?.replace('_', ' ') || 'Unknown'}
            </div>
            <div className="cfd-weeklyDateRange">
              {weeklyData.week_start} - {weeklyData.week_end}
            </div>
            <div className="cfd-weeklyItemCount">
              <strong>{weeklyData.total_items}</strong> items logged
            </div>
          </div>
        </div>

        {/* Daily breakdown */}
        <div className="cfd-breakdownSection">
          <div className="cfd-sectionTitle">Daily Breakdown</div>
          <div className="cfd-dailyGrid">
            {weeklyData.daily_logs?.map((day, idx) => {
              const dayGrade = getGradeStyle(day.daily_grade || 'N/A');
              return (
                <div key={idx} className="cfd-dayCard" style={{ backgroundColor: dayGrade.bg, border: `1px solid ${dayGrade.border}` }}>
                  <div className="cfd-dayCardLabel">
                    {formatDateOnly(day.log_date, { weekday: 'short' }, 'en-US')}
                  </div>
                  <div className="cfd-dayCardGrade" style={{ color: dayGrade.text }}>
                    {day.daily_grade || '\u2014'}
                  </div>
                  <div className="cfd-dayCardCount">
                    {day.total_items_logged} items
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Suggestions */}
        {weeklyData.suggestions?.length > 0 && (
          <div className="cfd-suggestionsList">
            <div className="cfd-suggestionsTitle">
              Tips to Improve
            </div>
            {weeklyData.suggestions.map((s, i) => (
              <div key={i} className="cfd-suggestion">
                <span>{'\u{1F331}'}</span> {s}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render monthly view
  const renderMonthlyView = () => {
    if (!monthlyData) return <div className="cfd-emptyState">No monthly data</div>;

    const gradeStyle = getGradeStyle(monthlyData.average_grade || 'N/A');

    return (
      <div>
        {/* Grade Card */}
        <div className="cfd-gradeCard" style={{ backgroundColor: gradeStyle.bg, border: `2px solid ${gradeStyle.border}` }}>
          <div className="cfd-gradeLabel" style={{ color: gradeStyle.text }}>
            {monthlyData.month} {monthlyData.year}
          </div>
          <div className="cfd-gradeValue" style={{ color: gradeStyle.text }}>
            {monthlyData.average_grade || '\u2014'}
          </div>
          <div className="cfd-gradeSubtext" style={{ color: gradeStyle.text }}>
            {monthlyData.total_items} items logged
          </div>
        </div>

        {/* Comparison to last month */}
        {monthlyData.comparison_to_last_month && (
          <div className="cfd-comparisonBanner" style={{ backgroundColor: monthlyData.comparison_to_last_month.improved ? '#D1FAE5' : '#FEE2E2' }}>
            <span className="cfd-comparisonIcon">
              {monthlyData.comparison_to_last_month.improved ? '\u{1F4C8}' : '\u{1F4C9}'}
            </span>
            <span className="cfd-comparisonText">
              {monthlyData.comparison_to_last_month.improved ? 'Improved' : 'Declined'} from last month
              ({monthlyData.comparison_to_last_month.last_month_grade})
            </span>
          </div>
        )}

        {/* Best/Worst days */}
        <div className="cfd-bestWorstGrid">
          {monthlyData.best_day && (
            <div className="cfd-bestDayCard">
              <div className="cfd-bestDayLabel">Best Day</div>
              <div className="cfd-bestDayGrade">
                {monthlyData.best_day.grade}
              </div>
              <div className="cfd-bestDayDate">
                {new Date(monthlyData.best_day.date).toLocaleDateString()}
              </div>
            </div>
          )}
          {monthlyData.worst_day && (
            <div className="cfd-worstDayCard">
              <div className="cfd-worstDayLabel">Needs Work</div>
              <div className="cfd-worstDayGrade">
                {monthlyData.worst_day.grade}
              </div>
              <div className="cfd-worstDayDate">
                {new Date(monthlyData.worst_day.date).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>

        {/* Weekly averages */}
        {monthlyData.weekly_averages?.length > 0 && (
          <div className="cfd-breakdownSection">
            <div className="cfd-sectionTitle">Weekly Progress</div>
            <div className="cfd-weeklyProgressGrid">
              {monthlyData.weekly_averages.map((week, idx) => {
                const weekGrade = getGradeStyle(week.grade || 'N/A');
                return (
                  <div key={idx} className="cfd-weekCard" style={{ backgroundColor: weekGrade.bg }}>
                    <div className="cfd-weekCardLabel">Week {idx + 1}</div>
                    <div className="cfd-weekCardGrade" style={{ color: weekGrade.text }}>
                      {week.grade}
                    </div>
                    <div className="cfd-weekCardCount">{week.items} items</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render history view
  const renderHistoryView = () => {
    if (!historyData?.history) return <div className="cfd-emptyState">No history data</div>;

    const maxItems = Math.max(...historyData.history.map(d => d.items || 0), 1);

    return (
      <div>
        <div className="cfd-sectionTitle">Last 30 Days</div>

        {/* Simple bar chart */}
        <div className="cfd-historyChart">
          {historyData.history.map((day, idx) => {
            const gradeStyle = getGradeStyle(day.grade || 'N/A');
            const height = day.items ? Math.max(20, (day.items / maxItems) * 130) : 10;

            return (
              <div
                key={idx}
                className="cfd-historyBar"
                style={{
                  height: `${height}px`,
                  backgroundColor: day.grade ? gradeStyle.border : '#E5E7EB'
                }}
                title={`${day.date}: Grade ${day.grade || 'N/A'}, ${day.items} items`}
              />
            );
          })}
        </div>

        {/* Legend */}
        <div className="cfd-historyLegend">
          {['A', 'B', 'C', 'D', 'E'].map(grade => {
            const gradeColor = getGradeStyle(grade);
            return (
              <div key={grade} className="cfd-legendItem">
                <span className="cfd-legendDot" style={{ backgroundColor: gradeColor.border }} />
                {grade}
              </div>
            );
          })}
        </div>

        {/* Stats */}
        <div className="cfd-statsGrid cfd-historyStats">
          <div className="cfd-statCard">
            <div className="cfd-statValue">
              {historyData.history.filter(d => d.grade).length}
            </div>
            <div className="cfd-statLabel">Days Tracked</div>
          </div>
          <div className="cfd-statCard">
            <div className="cfd-statValue">
              {historyData.history.reduce((sum, d) => sum + (d.items || 0), 0)}
            </div>
            <div className="cfd-statLabel">Total Items</div>
          </div>
          <div className="cfd-statCard">
            <div className="cfd-statValue">
              {historyData.history.filter(d => d.grade === 'A' || d.grade === 'A+').length}
            </div>
            <div className="cfd-statLabel">A-Grade Days</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="cfd-overlay">
      <div className="cfd-modal">
        <div className="cfd-header">
          <h2 className="cfd-title">Carbon Footprint Dashboard</h2>
          <button className="cfd-closeButton" onClick={onClose}>{'\u00D7'}</button>
        </div>

        <div className="cfd-tabs">
          {['today', 'weekly', 'monthly', 'history'].map(view => (
            <button
              key={view}
              className={activeView === view ? 'cfd-tab cfd-tab--active' : 'cfd-tab'}
              onClick={() => setActiveView(view)}
            >
              {view === 'today' && 'Today'}
              {view === 'weekly' && 'This Week'}
              {view === 'monthly' && 'This Month'}
              {view === 'history' && 'History'}
            </button>
          ))}
        </div>

        <div className="cfd-content">
          {error && <div className="cfd-error">{error}</div>}

          {loading ? (
            <div className="cfd-loadingState">
              <div className="cfd-loadingIcon">{'\u{1F30D}'}</div>
              Loading your eco data...
            </div>
          ) : (
            <>
              {activeView === 'today' && renderTodayView()}
              {activeView === 'weekly' && renderWeeklyView()}
              {activeView === 'monthly' && renderMonthlyView()}
              {activeView === 'history' && renderHistoryView()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarbonFootprintDashboard;
